import {
  ComponentType,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { useSession } from "next-auth/client";
import { Configure, InstantSearch } from "react-instantsearch-dom";
import { Box, useBreakpoints } from "@otrium/core";
import {
  connectStateResults,
  SearchState,
  StateResultsProvided,
} from "react-instantsearch-core";
import { useTheme, Theme } from "@emotion/react";
import { SearchClient } from "algoliasearch/dist/algoliasearch-lite";
import StickyBox from "react-sticky-box";
import { includes } from "lodash-es";
import { AlgoliaFilter } from "lib/algolia";
import { Locale } from "lib/Intl";
import { AppCtx, RankingInfo } from "src/contexts/app.context";
import { SearchConfiguration } from "src/types/graphql.d";
import {
  BrandViewCatalog,
  BrandViewProducts,
  Content,
} from "src/modules/brands/BrandView.styled";

import { AlgoliaFacet, MultiselectHitType } from "src/types/Algolia";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import {
  BRAND_FACET,
  CATEGORIES_FACET,
  COLOUR_FACET,
  CONSCIOUS_FACET,
  DISCOUNT_FACET,
  PATTERN_FACET,
  PRICE_FACET,
  PROMOTION_FACET,
  SIZE_FACET,
  STYLE_FACET,
} from "src/utils/algolia";
import { AlgoliaProductListHeader } from "src/molecules/AlgoliaProductListHeader";
import BrandsPromo from "src/molecules/BrandsPromo/BrandsPromo";
import { Department } from "src/types/department";
import { PLPType } from "src/types/PLPType.d";
import contextKeys from "src/utils/algolia/contextKeys";
import { MultiselectCategoryWrapper } from "./MultiselectCategories";
import { Loader } from "./MobileFilterView/Loader";
import { PromotionsFilter } from "./MobileFilterView/PromotionsFilter";
import { MobileFiltersButtonAndSortWrapper } from "./MobileFilterView/MobileFiltersButtonAndSortWrapper";
import { CategoriesTree } from "./DesktopFilterVeiw/CategoriesTree";
import {
  VirtualMenu,
  VirtualPriceRange,
  VirtualRefinementList,
} from "./VirtualInstantSearch";
import { CustomInfiniteHits } from "./CustomInfiniteHits";
import { MobileFilterView } from "./MobileFilterView";
import { SearchError } from "./SearchError";
import withAlgoliaState, { OptionsSortBy } from "./withAlgoliaState";
import { StyledMobileFiltersModal } from "./MobileFilterView/MobileFilters.styled";
import {
  LOGGED_IN_USER,
  NON_EMPTY_SEARCH,
  NON_LOGGED_IN_USER,
  WEB_SHOP,
} from "src/utils/algolia/analyticsTags";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { Modal } from "src/atoms/Modal";
import { ScreenLoader } from "src/molecules/ScreenLoader";
import findAlgoliaCategoriesTreeNode from "src/utils/findAlgoliaCategoriesTreeNode";
import BrandsPromoNonLoggedIn from "src/molecules/BrandsPromo/BrandsPromoNonLoggedIn";
import { CategoryTreeSlugOrder } from "src/organisms/AlgoliaProductList/AlgoliaProductList";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { PromotionContextProvider } from "src/contexts/promotion.context";

export interface SearchConfigurationProps {
  data: { searchConfigurationsWeb: SearchConfiguration };
  errors: { message: string }[];
}

interface InstantSearchWrapperProps {
  brandSlug?: string;
  brandName?: string;
  collectionId?: string;
  brandId?: string;
  categorySlug?: string;
  genderId: string;
  locale: Locale;
  searchState?: SearchState & {
    hitsPerPage: number;
    selectedCatSlug?: string[];
    categoriesTree?: MultiselectHitType[];
    keepSelectedIgnoreInActive?: { [p: string]: string[] };
  };
  searchTerm?: string;
  onSearchStateChange?: (state: SearchState) => void;
  syncWithURL?: (searchState: SearchState) => void;
  onInitialLoad?: (
    facets: AlgoliaFacet[],
    rankingInfo: RankingInfo,
    indexName?: string
  ) => void;
  onResultsChanged?: (facets: AlgoliaFacet[]) => void;
  defaultPriceRange?: { min: number; max: number };
  setStatePayload?: (
    payload: SearchState & { selectedCatSlug: string[] }
  ) => void;
  hideCategoryTree?: boolean;
  hidePromotions?: boolean;
  excludedFacetsList?: string[];
  plpType?: PLPType;
  limit?: number;
  setLoading?: (value: boolean) => void;
  defaultSort?: OptionsSortBy;
  department?: Department;
  initialFilters?: string[];
  slug?: string;
  title?: ReactNode;
  hideBreadcrumbs?: boolean;
  dataLoading?: boolean;
  noSearchResults?: ReactNode;
  categoryTreeSortOrder?: CategoryTreeSlugOrder;
  rankingInfo?: RankingInfo;
}

const SearchResults = connectStateResults<
  StateResultsProvided & {
    children?: ReactNode;
    onInitialLoad?: (
      facets: AlgoliaFacet[],
      rankingInfo: RankingInfo,
      indexName?: string
    ) => void;
    onResultsChanged?: (facets: AlgoliaFacet[]) => void;
    indexName?: string;
  }
>(({ allSearchResults, onInitialLoad, onResultsChanged, indexName }) => {
  if (onInitialLoad && allSearchResults) {
    const { abTestID, abTestVariantID, indexUsed } = allSearchResults;
    const rankingInfo = {
      abTestID,
      abTestVariantID,
      indexUsed,
    } as unknown as RankingInfo;
    if (indexUsed as unknown as string) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      onInitialLoad(allSearchResults.disjunctiveFacets, rankingInfo, indexName);
    }
  }

  useEffect(() => {
    if (onResultsChanged && allSearchResults) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      onResultsChanged(allSearchResults.disjunctiveFacets);
    }
  }, [allSearchResults?.disjunctiveFacets]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
});

const InstantSearchComponent: FC<InstantSearchWrapperProps> = ({
  genderId,
  locale,
  searchState,
  onSearchStateChange,
  syncWithURL,
  onInitialLoad,
  onResultsChanged,
  defaultPriceRange,
  setStatePayload,
  hideCategoryTree,
  hidePromotions,
  excludedFacetsList,
  plpType,
  defaultSort,
  initialFilters,
  department,
  categorySlug,
  slug,
  title,
  hideBreadcrumbs,
  dataLoading,
  searchTerm,
  noSearchResults,
  brandSlug,
  rankingInfo,
}) => {
  const { query } = useRouter();
  const openScreenLoader = query.signin === "true";
  const [session] = useSession();
  const isLoggedIn = useIsLoggedIn();
  const { isGhostDomain } = useContext(AppCtx);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchClient, setSearchClient] = useState<SearchClient | null>(null);
  const { isDesktop } = useBreakpoints();
  const theme: Theme = useTheme();
  const isBrandPage = plpType === PLPType.BRAND_PLP;
  const isClearancePage = plpType === PLPType.CLEARANCE_PLP;
  const isHotDeals = plpType === PLPType.HOT_DEALS;
  const isSearchPage = plpType === PLPType.SEARCH_PLP;
  const isAllProductsPage = plpType === PLPType.SHOP_TYPE;
  const { enableAugmentedSearch } = useFeatureFlags();
  const isMultiselectCategories =
    isBrandPage ||
    isClearancePage ||
    isHotDeals ||
    isSearchPage ||
    isAllProductsPage;
  const userToken = session?.user.member_id
    ? { userToken: session.user.member_id }
    : {};
  const configureProps = {
    maxValuesPerFacet: 1000,
    hitsPerPage: searchState?.hitsPerPage || 72,
    facets: ["*"],
    ruleContexts: [WEB_SHOP, contextKeys[plpType as PLPType]],
    analyticsTags: [
      WEB_SHOP,
      localeFix({ gender: genderId }),
      isLoggedIn ? LOGGED_IN_USER : NON_LOGGED_IN_USER,
      ...(searchTerm ? [NON_EMPTY_SEARCH] : []),
    ],
    filters: (initialFilters || []).join(" AND "),
    clickAnalytics: true,
    query: searchTerm,
    getRankingInfo: true,
    ...userToken,
  };

  const prefix = isGhostDomain ? "zz" : locale;
  const onMobileFiltersResetCB = useRef<() => void>();
  const sortBy = searchState?.sortBy || defaultSort;
  const isPopular = sortBy === "popular";
  const indexSuffix = isPopular ? "" : `${sortBy || ""}`;
  const augmentedSuffix =
    enableAugmentedSearch === "raw" && slug === "search"
      ? "_augmented_search"
      : "";

  const indexName = `${prefix}_products${indexSuffix}${augmentedSuffix}`;

  const handleError = () => {
    if (AlgoliaFilter.hasAttempts()) {
      AlgoliaFilter.clearClient();
      setSearchClient(null);
    }
  };

  useEffect(() => {
    let isActive = true;
    if (!searchClient) {
      void AlgoliaFilter.getClient().then((data) => {
        if (isActive) {
          setSearchClient(data);
        }
      });
    }
    return () => {
      isActive = false;
    };
  }, [searchClient]);

  const handleSortBy = useCallback(
    (value: string) => {
      if (onSearchStateChange && syncWithURL) {
        const nextSearchState = {
          ...searchState,
          sortBy: value,
        };
        onSearchStateChange(nextSearchState);
        syncWithURL(nextSearchState);
      }
    },
    [searchState] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleClose = useCallback(() => {
    if (syncWithURL) {
      syncWithURL(searchState as SearchState);
    }
  }, [searchState]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseMobileFilter = useCallback(() => {
    if (syncWithURL) {
      syncWithURL(searchState as SearchState);
    }
    setIsModalOpen(false);
  }, [searchState]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryTitle = useMemo(() => {
    return searchState?.categoriesTree
      ? findAlgoliaCategoriesTreeNode(
          searchState?.categoriesTree,
          slug || "",
          "slug"
        )?.name
      : slug?.split("-").join(" ");
  }, [slug, searchState]);

  if (!searchClient) {
    return null;
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
    >
      <AlgoliaProductListHeader
        locale={locale}
        genderId={genderId}
        department={department}
        categorySlug={categorySlug}
        slug={slug}
        categories={searchState?.categoriesTree}
        title={title}
        hideBreadcrumbs={hideBreadcrumbs}
      />
      <Content>
        {openScreenLoader && (
          /* onClose is a required property, but we don't allow the user to close this modal;
          that's why we use here onClose={() => {}} */
          <Modal onClose={() => {}} overFlow="visible" isOpen>
            <ScreenLoader />
          </Modal>
        )}
        <SearchError onError={handleError} />

        <Configure {...configureProps} />

        <SearchResults
          onInitialLoad={onInitialLoad}
          onResultsChanged={onResultsChanged}
          indexName={indexName}
        />
        {isDesktop && (
          <>
            <VirtualPriceRange attribute={PRICE_FACET} precision={2} />
            {!includes(excludedFacetsList, BRAND_FACET) && (
              <VirtualRefinementList attribute={BRAND_FACET} limit={200} />
            )}

            <VirtualRefinementList attribute={COLOUR_FACET} limit={100} />

            <VirtualRefinementList attribute={SIZE_FACET} limit={100} />
            {!includes(excludedFacetsList, PROMOTION_FACET) && (
              <VirtualMenu attribute={PROMOTION_FACET} />
            )}

            {!excludedFacetsList?.includes(STYLE_FACET) ? (
              <VirtualRefinementList attribute={STYLE_FACET} limit={20} />
            ) : null}

            <VirtualRefinementList attribute={PATTERN_FACET} limit={20} />
            {!includes(excludedFacetsList, CONSCIOUS_FACET) && (
              <VirtualRefinementList attribute={CONSCIOUS_FACET} />
            )}
            {!includes(excludedFacetsList, DISCOUNT_FACET) && (
              <VirtualMenu attribute={DISCOUNT_FACET} />
            )}
          </>
        )}
        {!isDesktop && (
          <Box>
            <Loader />
            <Box>
              {isBrandPage && (
                <Box display={["block", "block", "block", "none"]}>
                  <PromotionsFilter
                    attribute={PROMOTION_FACET}
                    syncStateWithUrl={handleClose}
                  />
                </Box>
              )}
              <MobileFiltersButtonAndSortWrapper
                handleOpenModal={setIsModalOpen}
                excludedFacetsList={excludedFacetsList}
                sortValue={
                  (searchState?.sortBy as string) ||
                  (!!defaultSort ? defaultSort : "popular")
                }
                handleSelect={handleSortBy}
                keepSelectedIgnoreInActive={
                  searchState?.keepSelectedIgnoreInActive
                }
                selectedCatSlug={searchState?.selectedCatSlug}
              />
            </Box>
            <StyledMobileFiltersModal isOpen={isModalOpen}>
              <MobileFilterView
                onResetFilters={() => onMobileFiltersResetCB.current?.()}
                setStatePayload={setStatePayload}
                selectedCatSlug={searchState?.selectedCatSlug || []}
                handleClose={handleCloseMobileFilter}
                genderId={genderId}
                excludedFacetsList={excludedFacetsList}
                isNewPlp={plpType === PLPType.NEW_IN}
                departmentSlug={department?.slug}
                categoriesTree={searchState?.categoriesTree}
                isMultiselectCategories={isMultiselectCategories}
                keepSelectedIgnoreInActive={
                  searchState?.keepSelectedIgnoreInActive
                }
              />
            </StyledMobileFiltersModal>
          </Box>
        )}
        <BrandViewCatalog>
          {isDesktop &&
            (!hideCategoryTree ||
              searchState?.categoriesTree?.length === 0) && (
              <StickyBox offsetTop={70} style={{ alignSelf: "start" }}>
                {isMultiselectCategories ? (
                  <Box
                    sx={{
                      position: "relative",
                      "ul>li": {
                        marginLeft: theme.space.space0,
                      },
                      "li>ul": {
                        marginLeft: theme.space.space8,
                      },
                    }}
                    as="aside"
                  >
                    <MultiselectCategoryWrapper
                      setStatePayload={setStatePayload}
                      selectedCatSlug={searchState?.selectedCatSlug || []}
                      attribute={CATEGORIES_FACET}
                      genderId={genderId}
                      limit={1000}
                      isNewPlp={false}
                      departmentSlug={department?.slug}
                      categoriesTree={searchState?.categoriesTree}
                      isMultiselectCategories={true}
                    />
                  </Box>
                ) : (
                  <aside>
                    <CategoriesTree
                      departmentSlug={department?.slug}
                      genderId={genderId}
                      categoriesTree={searchState?.categoriesTree}
                      isNewPlp={plpType === PLPType.NEW_IN}
                      plpType={plpType}
                    />
                  </aside>
                )}
              </StickyBox>
            )}
          <BrandViewProducts
            hideCategoryTree={
              hideCategoryTree || searchState?.categoriesTree?.length === 0
            }
          >
            {isLoggedIn &&
              !hidePromotions &&
              slug &&
              searchState?.categoriesTree && (
                <PromotionContextProvider>
                  <BrandsPromo skipTrackingViewed={dataLoading} slug={slug} />
                </PromotionContextProvider>
              )}
            {!isLoggedIn &&
              !hidePromotions &&
              slug &&
              searchState?.categoriesTree && (
                <BrandsPromoNonLoggedIn slug={slug} genderId={genderId} />
              )}

            <CustomInfiniteHits
              plpType={plpType}
              onMobileFiltersResetCB={(cb) =>
                (onMobileFiltersResetCB.current = cb)
              }
              configureProps={configureProps}
              isModalOpened={isModalOpen}
              searchClient={searchClient}
              indexName={indexName}
              searchState={searchState}
              onSearchStateChange={onSearchStateChange}
              onClose={handleClose}
              handleSortBy={handleSortBy}
              orderBy={
                searchState?.sortBy
                  ? searchState.sortBy
                  : defaultSort || "popular"
              }
              defaultPriceRange={
                defaultPriceRange as { min: number; max: number }
              }
              hideCategoryTree={hideCategoryTree}
              excludedFacetsList={excludedFacetsList}
              setStatePayload={setStatePayload}
              genderId={genderId}
              noSearchResults={noSearchResults}
              rankingInfo={rankingInfo}
              categorySlug={categorySlug}
              categoryTitle={categoryTitle}
            />
          </BrandViewProducts>
        </BrandViewCatalog>
      </Content>
    </InstantSearch>
  );
};

const InstantSearchWrapper = withAlgoliaState(
  InstantSearchComponent
) as ComponentType<InstantSearchWrapperProps>;

export { InstantSearchWrapper };
